import { useState } from "react";
import type { FogBugzCase } from "@/hooks/queries/use-fogbugz";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { AreaSubGroup } from "@/pages/fogbugz/components/area-sub-group";

interface ProjectSectionProps {
  projectName: string;
  areaMap: Map<string, FogBugzCase[]>;
  totalCount: number;
  onNavigate: (id: number) => void;
}

export function ProjectSection({
  projectName,
  areaMap,
  totalCount,
  onNavigate,
}: ProjectSectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Card className="overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={isOpen}
          >
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
            <span className="font-semibold text-sm flex-1">{projectName}</span>
            <Badge variant="secondary">{totalCount}</Badge>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-muted/20">
                <tr className="text-left">
                  <th className="px-4 py-2 font-medium text-xs text-muted-foreground">
                    Case #
                  </th>
                  <th className="px-4 py-2 font-medium text-xs text-muted-foreground">
                    Title
                  </th>
                  <th className="px-4 py-2 font-medium text-xs text-muted-foreground">
                    Status
                  </th>
                  <th className="px-4 py-2 font-medium text-xs text-muted-foreground">
                    Priority
                  </th>
                  <th className="px-4 py-2 font-medium text-xs text-muted-foreground">
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...areaMap.entries()].map(([areaName, cases]) => (
                  <AreaSubGroup
                    key={areaName}
                    areaName={areaName}
                    cases={cases}
                    onNavigate={onNavigate}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
